// The water ambience of the fast client (R499): the drifting fog blobs and
// the marine snow, with the ping-lit pass over the grains (R101).
//
// This is the port of `render/water.ts` — the fog and the snow halves of it.
// The background gradient and the vignette are screen passes of
// `gfx/worldquad.ts` and are already done, so only the two world-space
// crowds live here.
//
// THE ONE THING THIS FILE MUST GET RIGHT: the fog and the snow are scattered
// from ONE `mulberry32(99)` in the fog-then-snow order of
// `render/water.ts:105-160`, so the core world keeps the exact look it has.
// The two crowds are two layers here — the fog sits under the map and the
// snow over it (`LAYER_SLOTS`) — and two layers cannot share a generator
// object without one owning the other. So `scatterInto` runs the WHOLE
// sequence on every call and writes only the pool it was handed: the fog
// layer draws the fog half and lets the snow half fall on the floor, and the
// snow layer does the reverse. Each call is a few hundred draws of a
// generator, one time for each grid change or knob change, and the sequence
// is the original's by construction rather than by agreement.
//
// The counts follow the world's area share (R479) and then the `fogn` /
// `snown` knob, and the floors of presentation.toml hold whatever the knob
// says: a multiplier trims the crowd, it does not empty the world. `fog=0`
// and `snow=0` are what empty it, and the integrator answers those with
// `setVisible(false)` — an invisible layer is neither updated nor drawn
// (`fast/layer.ts`), so this file never reads a knob in `update`.
//
// One deliberate difference from the PixiJS layer: a blob or a grain outside
// the frame's cull rect packs no instance here. The PixiJS layer does not
// cull, because its sprites are a scene graph the renderer walks; this one
// writes instances by hand and pays for each one, so it culls. The particle
// still moves — the water off screen must not stop and then jump.

import {
  FOG_COUNT, FOG_COUNT_MIN, FOG_DRIFT_X, FOG_DRIFT_Y,
  FOG_SCALE_MIN, FOG_SCALE_SPAN,
  LIT_ALPHA_GAIN, LIT_BAND_PX, LIT_DECAY_PER_SEC, LIT_SCALE_GAIN,
  SNOW_ALPHA_MIN, SNOW_ALPHA_SPAN, SNOW_COUNT, SNOW_COUNT_MIN,
  SNOW_SINK_MIN, SNOW_SINK_SPAN,
} from '../../gen/presentation';
// The counts scale with the world (R479) and the menu previews ask for them
// too, so they live in an engine-free leaf that no caller copies.
import { fogCount, snowCount } from '../../render/ambience-counts';
import type { PingFront } from '../../render/sonar-types';
import { DEFAULT_WORLD, WORLD, WORLD_H, mulberry32 } from '../../world';
import type { AtlasEntry } from '../gfx/atlas';
import type { Knobs } from '../knobs';
import { outside, type DrawCtx, type FrameInfo, type Layer, type ViewRect } from '../layer';
import type { BatchSource, QuadSink } from './batches';

/** The empty front list, so a frame with no sweep allocates nothing. */
const NO_FRONTS: readonly PingFront[] = [];

/**
 * How many fog blobs the knob asks for.
 *
 * The multiplier scales the AREA-SCALED count, and the floor of
 * presentation.toml is applied after it, so `fogn=0` still leaves the
 * smallest board its light drift (R479).
 */
export function fogBlobs(fogn: number): number {
  return Math.max(FOG_COUNT_MIN, Math.round(fogCount() * fogn));
}

/** How many marine-snow grains the knob asks for, on the same rule. */
export function snowGrains(snown: number): number {
  return Math.max(SNOW_COUNT_MIN, Math.round(snowCount() * snown));
}

/**
 * The fog blobs as flat arrays. A scatter reuses the arrays when the count
 * has not changed, and the steady state allocates nothing at all.
 */
class FogPool {
  count = 0;
  x = new Float32Array(0);
  y = new Float32Array(0);
  scale = new Float32Array(0);
  vx = new Float32Array(0);
  vy = new Float32Array(0);

  resize(n: number): void {
    if (this.x.length !== n) {
      this.x = new Float32Array(n);
      this.y = new Float32Array(n);
      this.scale = new Float32Array(n);
      this.vx = new Float32Array(n);
      this.vy = new Float32Array(n);
    }
    this.count = n;
  }
}

/** The snow grains as flat arrays, `lit` being the R101 excitement in 0..1. */
class SnowPool {
  count = 0;
  x = new Float32Array(0);
  y = new Float32Array(0);
  vy = new Float32Array(0);
  sway = new Float32Array(0);
  phase = new Float32Array(0);
  base = new Float32Array(0);
  baseScale = new Float32Array(0);
  lit = new Float32Array(0);

  resize(n: number): void {
    if (this.x.length !== n) {
      this.x = new Float32Array(n);
      this.y = new Float32Array(n);
      this.vy = new Float32Array(n);
      this.sway = new Float32Array(n);
      this.phase = new Float32Array(n);
      this.base = new Float32Array(n);
      this.baseScale = new Float32Array(n);
    }
    // The excitement is cleared on every scatter, lengths equal or not: a
    // re-seeded grain is a new grain and must start dark.
    this.lit = new Float32Array(n);
    this.count = n;
  }
}

/**
 * The shared scatter. It draws the fog blobs and then the snow grains from
 * one `mulberry32(99)`, in the order and with the arithmetic of
 * `render/water.ts:105-160`, and writes only the pools it was given.
 *
 * A pool that is null still costs its draws, which is the point: the fog
 * layer and the snow layer each run the whole sequence and each keep their
 * own half, so neither can drift from the look the PixiJS client has.
 */
function scatterInto(
  fogs: number, snows: number, fog: FogPool | null, snow: SnowPool | null,
): void {
  const rand = mulberry32(99);
  // A blob's scale is tuned against core's ~5320 px world, and the scatter
  // places the same count at the same absolute size on any world, so a small
  // arcade board would take blobs wider than the whole board. The shrink
  // keeps one blob at the same fraction of the board on any map, and is
  // exactly 1 on the core world (R302, `render/water.ts:127`).
  const fogScale = Math.min(1, WORLD / DEFAULT_WORLD);
  fog?.resize(fogs);
  for (let i = 0; i < fogs; i++) {
    const x = rand() * WORLD;
    const y = rand() * WORLD_H;
    const s = (FOG_SCALE_MIN + rand() * FOG_SCALE_SPAN) * fogScale;
    const vx = (rand() - 0.5) * FOG_DRIFT_X;
    const vy = (rand() - 0.5) * FOG_DRIFT_Y;
    if (!fog) continue;
    fog.x[i] = x; fog.y[i] = y; fog.scale[i] = s; fog.vx[i] = vx; fog.vy[i] = vy;
  }

  snow?.resize(snows);
  for (let i = 0; i < snows; i++) {
    const x = rand() * WORLD;
    const y = rand() * WORLD_H;
    const base = SNOW_ALPHA_MIN + rand() * SNOW_ALPHA_SPAN;
    // The grain's own size band is a literal of the PixiJS layer
    // (`render/water.ts:152`), so it stays a literal here.
    const baseScale = 0.4 + rand() * 0.9;
    const vy = SNOW_SINK_MIN + rand() * SNOW_SINK_SPAN;
    // The sway span is a literal of the same line (`render/water.ts:157`).
    const sway = 4 + rand() * 8;
    const phase = rand() * Math.PI * 2;
    if (!snow) continue;
    snow.x[i] = x; snow.y[i] = y; snow.base[i] = base;
    snow.baseScale[i] = baseScale; snow.vy[i] = vy;
    snow.sway[i] = sway; snow.phase[i] = phase;
  }
}

/**
 * The fog: big, slow, translucent blobs that wrap on both axes.
 *
 * The blob is the atlas `fog` cell, which bakes the colour and the 0.16 peak
 * alpha of `render/water.ts:107-113` into its own texels, so the instance is
 * written white at alpha 1 and the sprite scale is the number the PixiJS
 * sprite sets.
 */
export class FogLayer implements Layer {
  readonly name = 'fog';

  private readonly quads: QuadSink;
  private readonly cell: AtlasEntry;
  private readonly pool = new FogPool();
  private shown = true;

  constructor(src: BatchSource) {
    this.quads = src.newQuads(FOG_COUNT);
    this.cell = src.sprite('fog');
    // The PixiJS layer scatters in its constructor, before any socket
    // exists, and re-scatters when the hello gave a different grid (R261).
    // The multiplier of an unstated knob is 1.
    this.apply(1, 1);
  }

  /**
   * Re-seeds the crowd for the grid as it is now and for the count knobs.
   * The integrator calls it on `engine.onGrid` and on a knob change.
   */
  scatter(knobs: Knobs): void {
    this.apply(knobs.fogn, knobs.snown);
  }

  /** How many blobs are scattered. The tests read it. */
  get count(): number { return this.pool.count; }

  update(frame: FrameInfo): void {
    const dt = frame.dtMs / 1000;
    const p = this.pool;
    const cull = frame.cull;
    // The cell is round, so one half-extent bounds it on both axes. A blob
    // is up to 2176 px across, and a point test on its centre would drop it
    // while it still covers half the screen.
    const halfCell = this.cell.w / 2;
    const q = this.quads;
    q.begin();
    for (let i = 0; i < p.count; i++) {
      const x = (p.x[i] + p.vx[i] * dt + WORLD) % WORLD;
      const y = (p.y[i] + p.vy[i] * dt + WORLD_H) % WORLD_H;
      p.x[i] = x;
      p.y[i] = y;
      const s = p.scale[i];
      const half = halfCell * s;
      if (outside(x, y, cull.x0 - half, cull.y0 - half, cull.x1 + half, cull.y1 + half)) continue;
      q.sprite(x, y, 0, this.cell, 0xffffff, 1, s);
    }
  }

  draw(ctx: DrawCtx): void {
    this.quads.flush(ctx.cam);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }

  private apply(fogn: number, snown: number): void {
    scatterInto(fogBlobs(fogn), snowGrains(snown), this.pool, null);
  }
}

/**
 * The marine snow, and the ping-lit response over it (R101).
 *
 * A grain sinks, sways, and wraps on the height alone — the PixiJS layer
 * wraps no grain on the width, because the sway is bounded and never carries
 * one off the map (`render/water.ts:175-178`).
 *
 * The lit pass is presentation over ambience the client already owns: it
 * lights PARTICLES, never contacts. Detection stays the server's to grant,
 * and this must not become a second channel that says something is out
 * there (R101).
 */
export class SnowLayer implements Layer {
  readonly name = 'snow';

  private readonly quads: QuadSink;
  private readonly cell: AtlasEntry;
  private readonly pool = new SnowPool();
  private shown = true;

  private fronts: readonly PingFront[] = NO_FRONTS;
  /** True while any grain is still glowing — it keeps the quiet water free. */
  private litAny = false;

  constructor(src: BatchSource) {
    this.quads = src.newQuads(SNOW_COUNT);
    this.cell = src.sprite('snow');
    this.apply(1, 1);
  }

  /** Re-seeds the crowd. `engine.onGrid` and a knob change call it. */
  scatter(knobs: Knobs): void {
    this.apply(knobs.fogn, knobs.snown);
    this.litAny = false;
  }

  /**
   * The live sweeps this frame, and whether the `lit` knob allows the pass.
   *
   * The array is kept and never copied: `SonarLayer.fronts()` reuses its own
   * pool inside the frame it was asked in. An empty list is the normal, free
   * case — the pass runs only while a sweep is out or a grain is still
   * coming down from one.
   *
   * `lit` false is exactly "no sweeps": the raise never happens and a grain
   * that is still glowing decays to dark instead of freezing there.
   */
  feed(fronts: readonly PingFront[], lit: boolean): void {
    this.fronts = lit ? fronts : NO_FRONTS;
  }

  /** How many grains are scattered. The tests read it. */
  get count(): number { return this.pool.count; }

  /** True while any grain still carries light. The tests read it. */
  get glowing(): boolean { return this.litAny; }

  update(frame: FrameInfo): void {
    const dt = frame.dtMs / 1000;
    const p = this.pool;
    for (let i = 0; i < p.count; i++) {
      p.y[i] = (p.y[i] + p.vy[i] * dt) % WORLD_H;
      // The 0.6 sway rate is a literal of `render/water.ts:177`.
      p.x[i] += Math.sin(frame.timeS * 0.6 + p.phase[i]) * p.sway[i] * dt;
    }
    this.light(dt);
    this.pack(frame.cull);
  }

  draw(ctx: DrawCtx): void {
    this.quads.flush(ctx.cam);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }

  private apply(fogn: number, snown: number): void {
    scatterInto(fogBlobs(fogn), snowGrains(snown), null, this.pool);
  }

  /**
   * The lit pass, with the early-out of `render/water.ts:183` kept word for
   * word: no sweep and nothing glowing costs one length test and a return.
   */
  private light(dt: number): void {
    const fronts = this.fronts;
    if (fronts.length === 0 && !this.litAny) return;
    const p = this.pool;
    let litAny = false;
    for (let i = 0; i < p.count; i++) {
      for (const w of fronts) {
        if (w.strength <= 0) continue;
        const d = Math.hypot(p.x[i] - w.x, p.y[i] - w.y);
        if (Math.abs(d - w.r) < LIT_BAND_PX && w.strength > p.lit[i]) p.lit[i] = w.strength;
      }
      if (p.lit[i] <= 0) continue;
      p.lit[i] = Math.max(0, p.lit[i] - LIT_DECAY_PER_SEC * dt);
      litAny = true;
    }
    this.litAny = litAny;
  }

  /**
   * The instances. The alpha and the scale are derived from the excitement
   * rather than stored, which is the same arithmetic the PixiJS layer writes
   * onto its sprite (`render/water.ts:193-194`) and one array less to keep.
   */
  private pack(cull: ViewRect): void {
    const p = this.pool;
    const q = this.quads;
    q.begin();
    for (let i = 0; i < p.count; i++) {
      const x = p.x[i];
      const y = p.y[i];
      if (outside(x, y, cull.x0, cull.y0, cull.x1, cull.y1)) continue;
      const lit = p.lit[i];
      q.sprite(
        x, y, 0, this.cell, 0xffffff,
        p.base[i] + LIT_ALPHA_GAIN * lit,
        p.baseScale[i] * (1 + LIT_SCALE_GAIN * lit),
      );
    }
  }
}
